import React, { useRef, useCallback, useState, useEffect } from "react";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  Heading2,
  Heading3,
  Type,
  Minus,
  Link,
  Unlink,
} from "lucide-react";
import { sanitizeHtml } from "../lib/sanitize";

const ToolbarButton = ({ onClick, active, children, title }) => (
  <button
    type="button"
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    className={`p-1.5 rounded transition-colors ${
      active
        ? "bg-white/20 text-white"
        : "text-zinc-400 hover:text-white hover:bg-white/10"
    }`}
    title={title}
  >
    {children}
  </button>
);

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const savedSelectionRef = useRef(null);
  const initializedRef = useRef(false);
  const internalChangeRef = useRef(false);

  const notifyChange = useCallback(() => {
    if (editorRef.current) {
      internalChangeRef.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // Only set HTML on initial mount or when value changes externally
  useEffect(() => {
    if (editorRef.current && !initializedRef.current) {
      editorRef.current.innerHTML = sanitizeHtml(value || "");
      initializedRef.current = true;
    }
  }, []);

  // Sync external value changes (parent-driven updates such as Duplicate / Reset).
  // We use internalChangeRef to skip the sync triggered by our own onChange,
  // so the caret position is preserved while the user types.
  useEffect(() => {
    if (!editorRef.current || !initializedRef.current) return;
    if (internalChangeRef.current) {
      internalChangeRef.current = false;
      return;
    }
    const incoming = sanitizeHtml(value || "");
    if (editorRef.current.innerHTML !== incoming) {
      editorRef.current.innerHTML = incoming;
    }
  }, [value]);

  const execCommand = useCallback((command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    notifyChange();
  }, [notifyChange]);

  const handleInput = useCallback(() => {
    notifyChange();
  }, [notifyChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const raw = e.clipboardData.getData("text/html") || e.clipboardData.getData("text/plain");
    const clean = sanitizeHtml(raw);
    document.execCommand("insertHTML", false, clean);
    notifyChange();
  }, [notifyChange]);

  const formatBlock = useCallback((tag) => {
    editorRef.current?.focus();
    document.execCommand("formatBlock", false, tag);
    notifyChange();
  }, [notifyChange]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    if (savedSelectionRef.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  };

  const handleLinkClick = () => {
    saveSelection();
    const sel = window.getSelection();
    const selectedText = sel.toString();
    if (!selectedText) return;
    setLinkUrl("https://");
    setShowLinkInput(true);
  };

  const applyLink = () => {
    if (!linkUrl || linkUrl === "https://") {
      setShowLinkInput(false);
      return;
    }
    restoreSelection();
    document.execCommand("createLink", false, linkUrl);
    if (editorRef.current) {
      const links = editorRef.current.querySelectorAll("a");
      links.forEach((a) => {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      });
      notifyChange();
    }
    setShowLinkInput(false);
    setLinkUrl("");
  };

  const removeLink = () => {
    execCommand("unlink");
  };

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-zinc-950" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-white/10 bg-zinc-900/80 flex-wrap">
        <ToolbarButton onClick={() => formatBlock("p")} title="Parágrafo">
          <Type className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => formatBlock("h2")} title="Título Grande">
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => formatBlock("h3")} title="Título Médio">
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolbarButton onClick={() => execCommand("bold")} title="Negrito">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand("italic")} title="Itálico">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand("underline")} title="Sublinhado">
          <Underline className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolbarButton onClick={() => execCommand("insertUnorderedList")} title="Lista">
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand("insertOrderedList")} title="Lista Numerada">
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolbarButton onClick={() => execCommand("justifyLeft")} title="Alinhar Esquerda">
          <AlignLeft className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand("justifyCenter")} title="Centrar">
          <AlignCenter className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolbarButton onClick={handleLinkClick} title="Inserir Link (selecione texto primeiro)">
          <Link className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={removeLink} title="Remover Link">
          <Unlink className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolbarButton onClick={() => execCommand("insertHorizontalRule")} title="Linha Horizontal">
          <Minus className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {/* Link URL input bar */}
      {showLinkInput && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-zinc-900/60">
          <Link className="w-4 h-4 text-zinc-400 shrink-0" />
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applyLink(); if (e.key === "Escape") setShowLinkInput(false); }}
            placeholder="https://exemplo.com/termos"
            className="flex-1 bg-transparent text-zinc-200 text-sm outline-none placeholder:text-zinc-500"
            autoFocus
            data-testid="link-url-input"
          />
          <button
            onClick={applyLink}
            className="text-xs font-medium px-3 py-1 rounded bg-green-600 text-white hover:bg-green-500 transition-colors"
            data-testid="link-apply-btn"
          >
            Aplicar
          </button>
          <button
            onClick={() => setShowLinkInput(false)}
            className="text-xs font-medium px-3 py-1 rounded bg-zinc-700 text-zinc-300 hover:bg-zinc-600 transition-colors"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        dir="ltr"
        onInput={handleInput}
        onPaste={handlePaste}
        className="min-h-[200px] max-h-[400px] overflow-y-auto p-4 text-zinc-300 leading-relaxed focus:outline-none rich-editor-content"
        style={{ direction: "ltr", textAlign: "left", unicodeBidi: "plaintext" }}
        data-placeholder={placeholder || "Escreva aqui..."}
        suppressContentEditableWarning
        data-testid="rich-text-editor-content"
      />
    </div>
  );
};

export default RichTextEditor;
